/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { Button, Input, InputLabel, Modal, Select } from '@scuf/common';
import UtilityAccountAction from '../../../context/UtilityAccountContext/UtilityAccountActions';
import { useUtilityAccountContext } from '../../../context/UtilityAccountContext/UtilityAccountContext';
import './ReverifyTheme.scss';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { IUpdateAccount } from '../IUtilityServiceSettingsProps';
import useConnectorForPost from '../../shared/useConnectorAndDynamicProps/useConnectorForPost';
import { ICommonProps } from '../../UtilityServiceModal/IUtilityService';
import {
  getActionForUtilityStatus,
  getActionModalInfo,
  getRequestBodyUtilityAction,
  validateInputfields
} from '../../shared/Utils/Utils';
import { ApiContext } from '../../../context/ApiContext';
import {
  getNotifyInfo,
  getNotifyUtilityInfo
} from '../../shared/Utils/UtilitySettings/UtilitySettingsUtils';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';

function Reverify(props: ICommonProps) {
  const { bearerToken, requestContext, parentPutAxios } = props;
  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  const [state, dispatch] = useUtilityAccountContext();
  let {
    showReverifyModal,
    selectedUtility,
    selectedUtility: {
      accountNumber,
      utilityType,
      status: selectedUtilityStatus,
      idsStatus
    }
  } = state;
  const isUpdateAccount =
    selectedUtilityStatus == 'ACCOUNT_MISSING' ? true : false;
  const { header, messageNote, displayAccountInfo } = getActionModalInfo(
    selectedUtilityStatus,
    idsStatus
  );
  const initState = {
    username: '',
    password: '',
    usernameError: '',
    passwordError: '',
    accountNumber: accountNumber,
    accountNumberError: ''
  };
  const [Credentials, setCredentials] = useState<IUpdateAccount>(initState);
  const [Submitted, setSubmitted] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const closeModal = () => {
    dispatch(UtilityAccountAction.resetSelectedUtilityInfo());
    setSubmitted(false);
    dispatch(UtilityAccountAction.hideReverifyModal());
  };
  useEffect(() => {
    setCredentials({ ...Credentials, accountNumber });
  }, [accountNumber]);

  useEffect(() => {
    setCredentials({ ...initState });
  }, [showReverifyModal]);

  useEffect(() => {
    if (Submitted) isValid();
  }, [Credentials.username, Credentials.password, Credentials.accountNumber]);

  const checkForSpecialCharacter = (text: string) =>
    text.replace(new RegExp(/[`@.!#$%^&*()+\_=\[\]{};':"\\|,<>\/?~]/, 'g'), '');

  const isValid = () => {
    const { Valid, tempreverify } = validateInputfields(
      Credentials,
      isUpdateAccount
    );
    setCredentials({ ...tempreverify });
    return Valid;
  };
  const action = getActionForUtilityStatus(selectedUtilityStatus, idsStatus);
  const apiContext = useContext(ApiContext);
  const { notifyMessage } = apiContext;
  const handleSubmit = async () => {
    setSubmitted(true);
    const valid = isValid();
    if (valid) {
      const requestBody = getRequestBodyUtilityAction(
        action,
        selectedUtility,
        Credentials
      );
      setSubmitLoading(true);
      const responce = await parentPutAxios(action, requestBody);
      setSubmitLoading(false);
      closeModal();
      const { notifyDescription, notifyType } = getNotifyUtilityInfo(responce);
      notifyMessage && notifyMessage(t(notifyDescription), notifyType);
    }
  };
  return (
    <Modal
      className="emaas-update-reverify"
      size="large"
      closeIcon={true}
      onClose={closeModal}
      open={showReverifyModal}
      closeOnDimmerClick={false}
    >
      <div className="reverify-modal-header">{t(header)}</div>
      <Modal.Content className="reverify-modal-content">
        <p className="service-provider-note">{t(messageNote)}</p>
        <div className="signin-username">
          <Input
            placeholder="ex. john@example.com"
            onChange={(username) =>
              setCredentials({ ...Credentials, username })
            }
            label={t(d.USERNAME)}
            indicator="required"
            error={Credentials.usernameError}
            value={Credentials.username}
          />
        </div>
        <div className="signin-password">
          <Input
            placeholder="xxxxxxxxxxx"
            label={t(d.PASSWORD)}
            type="password"
            disablePasswordToggle={true}
            onChange={(password) =>
              setCredentials({ ...Credentials, password })
            }
            indicator="required"
            error={Credentials.passwordError}
            value={Credentials.password}
          />
        </div>
        {displayAccountInfo && (
          <div className="account-details">
            <Input
              className="account-number"
              // type="number"
              placeholder=""
              disabled={isUpdateAccount ? false : true}
              value={Credentials.accountNumber}
              label={t(d.ACCNUMBER)}
              indicator={isUpdateAccount ? 'required' : undefined}
              error={Credentials.accountNumberError}
              onChange={(enteredAccount) =>
                setCredentials({
                  ...Credentials,
                  accountNumber: checkForSpecialCharacter(enteredAccount)
                })
              }
            />
            <div className={'utility'}>
              <InputLabel className="utility-label" label={t(d.UtilityType)} />
              <Select
                disabled={true}
                placeholder={t(d.SELECTUTILITY)}
                value={utilityType}
                options={[{ text: utilityType, value: utilityType }]}
              />
            </div>
          </div>
        )}
        <div className="reverify-modal-footer">
          <Button
            type="secondary"
            content={t(d.CANCEL)}
            onClick={closeModal}
          ></Button>
          <Button
            type="primary"
            content={t(d.SUBMIT)}
            loading={submitLoading}
            onClick={handleSubmit}
          ></Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}
export default Reverify;
